import { FirebaseAuth } from "utils/firebase";

export async function memoryRequest(
  path = "/memories",
  { method = "GET", body, signal } = {},
) {
  // Bound read requests, including token acquisition. Mutation requests retain
  // their existing receipt semantics and are never automatically retried.
  const controller = new AbortController();
  let abortError = new DOMException("Aborted", "AbortError");
  const abort = () => {
    abortError = signal.reason || abortError;
    controller.abort();
  };
  if (signal?.aborted) abort();
  else signal?.addEventListener("abort", abort, { once: true });
  let timeout;
  const cancelled = new Promise((_, reject) => {
    const onAbort = () => reject(abortError);
    if (controller.signal.aborted) onAbort();
    else controller.signal.addEventListener("abort", onAbort, { once: true });
  });
  if (method === "GET" || path.startsWith("/memory-submissions"))
    timeout = setTimeout(
      () => {
        abortError = Object.assign(
          new Error(
            method === "GET"
              ? "Loading memories took too long. Please retry."
              : "The save response took too long. Checking whether your text was saved.",
          ),
          { code: "request_timeout" },
        );
        controller.abort();
      },
      method === "GET" ? 20000 : 10000,
    );
  const request = async () => {
    const account = FirebaseAuth.currentUser;
    const token = await account?.getIdToken();
    if (controller.signal.aborted) throw abortError;
    if (FirebaseAuth.currentUser?.uid !== account?.uid)
      throw new Error("The signed-in account changed. Please reload.");
    if (!token) throw new Error("Please sign in to manage your memories.");
    const response = await fetch(
      process.env.REACT_APP_GT_WEBHOOK_API_URL + "/assistant" + path,
      {
        method,
        cache: "no-store",
        signal: controller.signal,
        headers: {
          "content-type": "application/json",
          authorization: "Bearer " + token,
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      },
    );
    const data = await response.json();
    if (FirebaseAuth.currentUser?.uid !== account?.uid)
      throw new Error("The signed-in account changed. Please reload.");
    if (!response.ok)
      throw Object.assign(
        new Error(data.error || "Memory is temporarily unavailable."),
        { code: data.code, status: response.status },
      );
    return data;
  };
  try {
    return await Promise.race([request(), cancelled]);
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}
